import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLoads } from "@/context/LoadsContext";
import { AppShell } from "@/components/AppShell";
import { StatusBadge } from "@/components/StatusBadge";
import { generateRateConfirmationPDF } from "@/utils/generatePDF";
import { Plus, Search, Phone, MapPin, ArrowRight, DollarSign, FileDown, TrendingUp, Package, Truck as TruckIcon } from "lucide-react";

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const StatCard = ({ icon: Icon, label, value, accent, testId }) => (
  <div className="rd-card p-5" data-testid={testId}>
    <div className="flex items-center justify-between mb-3">
      <div className="rd-label !mb-0">{label}</div>
      <div
        className="h-8 w-8 rounded-lg flex items-center justify-center border-2 border-black"
        style={{ background: accent, boxShadow: "2px 2px 0 0 rgba(255,255,255,0.85)" }}
      >
        <Icon size={16} color="#0A0A0A" strokeWidth={3} />
      </div>
    </div>
    <div className="font-display font-black text-3xl tracking-tight">{value}</div>
  </div>
);

const FILTERS = ["All", "Assigned", "Dispatched", "In Transit", "Delivered"];

export default function Dashboard() {
  const { loads } = useLoads();
  const navigate = useNavigate();
  const [filter, setFilter] = useState("All");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    return loads.filter((l) => {
      const matchesStatus = filter === "All" || l.status === filter;
      const q = query.trim().toLowerCase();
      const matchesQuery =
        !q ||
        l.loadNumber.toLowerCase().includes(q) ||
        l.carrierName.toLowerCase().includes(q) ||
        l.customer.toLowerCase().includes(q) ||
        l.origin.toLowerCase().includes(q) ||
        l.destination.toLowerCase().includes(q);
      return matchesStatus && matchesQuery;
    });
  }, [loads, filter, query]);

  const stats = useMemo(() => {
    const totalRevenue = loads.reduce((s, l) => s + Number(l.customerRate || 0), 0);
    const totalMargin = loads.reduce(
      (s, l) => s + (Number(l.customerRate || 0) - Number(l.carrierPay || 0) - Number(l.fsc || 0)),
      0,
    );
    const inTransit = loads.filter((l) => l.status === "In Transit" || l.status === "Dispatched").length;
    return { totalRevenue, totalMargin, inTransit, active: loads.length };
  }, [loads]);

  return (
    <AppShell>
      <div className="p-6 lg:p-10 max-w-[1400px] mx-auto rd-fade-up">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-8">
          <div>
            <div className="rd-section-tag mb-3">◆ Broker Dashboard</div>
            <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tight">
              Active Booked Loads
            </h1>
            <p className="text-zinc-400 mt-2 text-sm max-w-xl">
              Every freight you've assigned to a carrier — track status, margins, and issue rate confirmations from one cockpit.
            </p>
          </div>
          <button
            onClick={() => navigate("/new-load")}
            className="rd-btn-primary px-6 py-3.5 flex items-center gap-2 text-sm self-start lg:self-end rd-pulse"
            data-testid="book-new-load-button"
          >
            <Plus size={18} strokeWidth={3} />
            Book New Load
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard testId="stat-active" icon={Package} label="Active Loads" value={stats.active} accent="#F9DB81" />
          <StatCard testId="stat-transit" icon={TruckIcon} label="On The Road" value={stats.inTransit} accent="#8B5CF6" />
          <StatCard testId="stat-revenue" icon={DollarSign} label="Customer Revenue" value={fmtMoney(stats.totalRevenue)} accent="#10B981" />
          <StatCard testId="stat-margin" icon={TrendingUp} label="Broker Margin" value={fmtMoney(stats.totalMargin)} accent="#3B82F6" />
        </div>

        {/* Filter bar */}
        <div className="rd-card p-4 mb-6 flex flex-col lg:flex-row gap-4 lg:items-center justify-between">
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-4 py-1.5 rounded-full text-xs font-display font-bold uppercase tracking-wider border-2 transition-all
                  ${filter === f
                    ? "bg-[#F9DB81] text-black border-black shadow-[2px_2px_0_0_rgba(255,255,255,0.85)]"
                    : "border-white/15 text-zinc-400 hover:border-white/40 hover:text-white"}`}
              >
                {f}
              </button>
            ))}
          </div>
          <div className="relative w-full lg:w-72">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              type="text"
              placeholder="Search loads, carriers, lanes..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="rd-input pl-9 py-2.5 text-sm"
              data-testid="dashboard-search-input"
            />
          </div>
        </div>

        {/* Loads grid / table */}
        <div className="rd-card overflow-hidden" data-testid="loads-table">
          {/* Header row */}
          <div className="hidden lg:grid grid-cols-[1fr_1.4fr_1fr_1.6fr_1fr_1fr_auto] gap-4 px-6 py-4 border-b-2 border-white/10 bg-black/30">
            {["Load #", "Carrier", "Driver", "Origin → Destination", "Margin", "Status", ""].map((h) => (
              <div key={h} className="rd-label !mb-0 text-zinc-500">{h}</div>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div className="p-16 text-center" data-testid="empty-state">
              <div className="font-display text-2xl font-bold mb-1">No loads match your filter</div>
              <div className="text-zinc-500 text-sm">Try clearing the search or selecting "All".</div>
            </div>
          ) : (
            filtered.map((load) => {
              const margin = Number(load.customerRate || 0) - Number(load.carrierPay || 0) - Number(load.fsc || 0);
              const marginPct = load.customerRate ? (margin / Number(load.customerRate)) * 100 : 0;
              return (
                <div
                  key={load.id}
                  className="rd-table-row grid grid-cols-1 lg:grid-cols-[1fr_1.4fr_1fr_1.6fr_1fr_1fr_auto] gap-4 px-6 py-5 border-b border-white/5 last:border-b-0 items-center"
                  data-testid={`load-row-${load.loadNumber}`}
                >
                  {/* Load # */}
                  <div>
                    <div className="font-display font-extrabold text-base">{load.loadNumber}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">{load.customer}</div>
                  </div>

                  {/* Carrier */}
                  <div>
                    <div className="font-semibold text-sm">{load.carrierName}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5 uppercase tracking-wider">{load.carrierMC}</div>
                  </div>

                  {/* Driver phone */}
                  <div>
                    <div className="text-sm font-medium">{load.driverName}</div>
                    <div className="flex items-center gap-1.5 text-xs text-zinc-400 mt-0.5">
                      <Phone size={11} /> {load.driverPhone}
                    </div>
                  </div>

                  {/* Lane */}
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin size={13} className="text-[#F9DB81] shrink-0" />
                    <span className="font-medium">{load.origin}</span>
                    <ArrowRight size={13} className="text-zinc-500 shrink-0" />
                    <span className="font-medium">{load.destination}</span>
                  </div>

                  {/* Margin */}
                  <div>
                    <div className="font-display font-extrabold text-base text-[#10B981]">
                      {fmtMoney(margin)}
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">{marginPct.toFixed(1)}% on rate</div>
                  </div>

                  {/* Status */}
                  <div><StatusBadge status={load.status} /></div>

                  {/* Action */}
                  <button
                    onClick={() => generateRateConfirmationPDF(load)}
                    className="rd-btn-secondary px-3 py-2 text-xs flex items-center gap-1.5"
                    data-testid={`row-pdf-${load.loadNumber}`}
                    title="Download Rate Confirmation"
                  >
                    <FileDown size={13} strokeWidth={3} />
                    PDF
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </AppShell>
  );
}
